import Cookies from 'universal-cookie';

import { pushEcommerceEvent } from './gtm';

/**
 * PG 리다이렉트 후 완료 화면에는 주문 정보가 없어서,
 * 결제 요청 직전에 purchase 이벤트 데이터를 쿠키에 저장해 두었다가 완료 화면에서 발화한다.
 * 결제 실패 시에는 같은 데이터로 payment_fail 을 발화한다.
 */
export type PendingPurchaseKind = 'GIFT' | 'PREPAID_CARD_PURCHASE' | 'PREPAID_CARD_CHARGE';

const COOKIE_KEY = 'pendingPurchase';
const MAX_AGE = 60 * 30;

const cookies = new Cookies(null, { path: '/' });

interface PendingPurchase {
    kind: PendingPurchaseKind;
    ecommerce: Record<string, unknown>;
}

export function savePendingPurchase(kind: PendingPurchaseKind, ecommerce: Record<string, unknown>) {
    const data: PendingPurchase = { kind, ecommerce };
    cookies.set(COOKIE_KEY, JSON.stringify(data), {
        maxAge: MAX_AGE,
        path: '/',
    });
}

/** 저장된 이벤트 데이터를 꺼내고 삭제한다. */
function popPendingPurchase(kind: PendingPurchaseKind) {
    try {
        const raw = cookies.get(COOKIE_KEY, { doNotParse: true });
        if (!raw) return undefined;
        const data: PendingPurchase = JSON.parse(raw);
        if (data.kind !== kind) return undefined;
        cookies.remove(COOKIE_KEY, { path: '/' });
        return data.ecommerce;
    } catch (e) {
        console.error(e);
        return undefined;
    }
}

/**
 * 저장된 purchase 이벤트를 1회 발화하고 삭제한다.
 * transactionId 를 넘기면 저장된 transaction_id 대신 사용한다. (refund 와 같은 주문번호를 쓰기 위함)
 */
export function pushPendingPurchase(kind: PendingPurchaseKind, transactionId?: string) {
    const ecommerce = popPendingPurchase(kind);
    if (!ecommerce) return;
    pushEcommerceEvent('purchase', {
        ...ecommerce,
        ...(transactionId && { transaction_id: transactionId }),
    });
}

/**
 * 저장된 데이터로 payment_fail 이벤트를 발화하고 삭제한다.
 * 저장된 데이터가 없으면 fallback 으로 발화한다.
 */
export function pushPendingPaymentFail(
    kind: PendingPurchaseKind,
    fallback: Record<string, unknown>,
) {
    const ecommerce = popPendingPurchase(kind);
    pushEcommerceEvent('payment_fail', ecommerce
        ? { ...ecommerce, purchase_type: ecommerce.purchase_type || kind }
        : { purchase_type: kind, ...fallback });
}
